import { ref, computed, watch } from 'vue'

export type ThemePref = 'system' | 'light' | 'dark'
export type Accent = 'purple' | 'indigo' | 'teal' | 'coral' | 'lime'
export type Radius = 'sharp' | 'normal' | 'round'
export type Density = 'compact' | 'comfortable'

const STORAGE_KEY = 'ph.designSystem.v1'

type Tweaks = {
  themePref: ThemePref
  accent: Accent
  radius: Radius
  density: Density
}

const defaults: Tweaks = {
  themePref: 'system',
  accent: 'purple',
  radius: 'normal',
  density: 'comfortable',
}

function loadTweaks(): Tweaks {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return { ...defaults, ...JSON.parse(raw) }
  } catch {
    /* ignore */
  }
  return { ...defaults }
}

const tweaks = ref<Tweaks>(loadTweaks())
const systemDark = ref(false)
let mql: MediaQueryList | null = null

function syncSystemDark() {
  systemDark.value = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

const resolvedMode = computed<'light' | 'dark'>(() => {
  if (tweaks.value.themePref === 'dark') return 'dark'
  if (tweaks.value.themePref === 'light') return 'light'
  return systemDark.value ? 'dark' : 'light'
})

const isDark = computed(() => resolvedMode.value === 'dark')

function applyToDom() {
  const r = document.documentElement
  r.setAttribute('data-theme', resolvedMode.value)
  r.setAttribute('data-accent', tweaks.value.accent)
  r.setAttribute('data-radius', tweaks.value.radius)
  r.setAttribute('data-density', tweaks.value.density)
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tweaks.value))
  } catch {
    /* ignore */
  }
}

function setTweak<K extends keyof Tweaks>(key: K, value: Tweaks[K]) {
  tweaks.value = { ...tweaks.value, [key]: value }
}

function patchTweaks(patch: Partial<Tweaks>) {
  tweaks.value = { ...tweaks.value, ...patch }
}

watch(
  tweaks,
  () => {
    applyToDom()
    persist()
  },
  { deep: true },
)

watch(resolvedMode, applyToDom)

// Token accessor — exposes the same fields used in inline styles.
const theme = computed(() => {
  const dark = isDark.value
  const accentMap = {
    purple: '#7C5CFF',
    indigo: '#5B6CFF',
    teal: '#14B8A6',
    coral: '#F97066',
    lime: '#9CD36A',
  } as const
  const accentDarkMap = {
    purple: '#6445F2',
    indigo: '#4555EA',
    teal: '#0E9488',
    coral: '#E04F45',
    lime: '#7FB74A',
  } as const
  const accentSoftLight = {
    purple: '#EFEAFF',
    indigo: '#E9ECFF',
    teal: '#D6F5F0',
    coral: '#FFE4E1',
    lime: '#E7F5D6',
  } as const
  const accentSoftDark = {
    purple: 'rgba(124,92,255,0.18)',
    indigo: 'rgba(91,108,255,0.18)',
    teal: 'rgba(20,184,166,0.18)',
    coral: 'rgba(249,112,102,0.18)',
    lime: 'rgba(156,211,106,0.20)',
  } as const
  const accentBorderDark = {
    purple: 'rgba(124,92,255,0.32)',
    indigo: 'rgba(91,108,255,0.32)',
    teal: 'rgba(20,184,166,0.34)',
    coral: 'rgba(249,112,102,0.34)',
    lime: 'rgba(156,211,106,0.36)',
  } as const

  const accent = accentMap[tweaks.value.accent]
  const accentDark = accentDarkMap[tweaks.value.accent]
  const accentSoft = dark ? accentSoftDark[tweaks.value.accent] : accentSoftLight[tweaks.value.accent]
  const accentBorder = dark ? accentBorderDark[tweaks.value.accent] : 'rgba(20,17,14,0.06)'
  const accentOn = tweaks.value.accent === 'lime' ? '#1A2410' : '#FFFFFF'

  const light = {
    bg: '#F6F4EF',
    bg2: '#FFFFFF',
    surface: '#FFFFFF',
    surface2: '#F3F0EA',
    surfaceHi: '#FBFAF6',
    border: 'rgba(20,20,20,0.08)',
    borderStrong: 'rgba(20,20,20,0.14)',
    text: '#14110E',
    text2: 'rgba(20,17,14,0.68)',
    text3: 'rgba(20,17,14,0.45)',
    chipBg: 'rgba(20,17,14,0.05)',
    chipBorder: 'rgba(20,17,14,0.08)',
    glass: 'rgba(255,255,255,0.72)',
    glassBorder: 'rgba(255,255,255,0.9)',
    shadowSm: '0 1px 2px rgba(17,12,8,0.06)',
    shadow: '0 2px 8px rgba(17,12,8,0.06), 0 12px 32px -12px rgba(17,12,8,0.12)',
    shadowLg: '0 20px 60px -20px rgba(17,12,8,0.22)',
    success: '#2F9E6E',
    successSoft: '#D6F0E3',
    warn: '#C98116',
    warnSoft: '#FBEAD0',
    danger: '#E04F45',
    dangerSoft: '#FFE4E1',
    tabBar: 'rgba(246,244,239,0.78)',
  }

  const darkVals = {
    bg: '#0B0A0D',
    bg2: '#111014',
    surface: '#17161B',
    surface2: '#1E1C22',
    surfaceHi: '#22202A',
    border: 'rgba(255,255,255,0.07)',
    borderStrong: 'rgba(255,255,255,0.14)',
    text: '#F4F2EE',
    text2: 'rgba(244,242,238,0.68)',
    text3: 'rgba(244,242,238,0.42)',
    chipBg: 'rgba(255,255,255,0.06)',
    chipBorder: 'rgba(255,255,255,0.08)',
    glass: 'rgba(30,28,34,0.62)',
    glassBorder: 'rgba(255,255,255,0.08)',
    shadowSm: '0 1px 2px rgba(0,0,0,0.4)',
    shadow: '0 2px 8px rgba(0,0,0,0.35), 0 16px 40px -16px rgba(0,0,0,0.6)',
    shadowLg: '0 24px 70px -18px rgba(0,0,0,0.7)',
    success: '#46D39A',
    successSoft: 'rgba(70,211,154,0.15)',
    warn: '#F0B341',
    warnSoft: 'rgba(240,179,65,0.16)',
    danger: '#F97066',
    dangerSoft: 'rgba(249,112,102,0.18)',
    tabBar: 'rgba(11,10,13,0.72)',
  }

  const base = dark ? darkVals : light

  const radiusMap = {
    sharp: { card: 10, pill: 10, input: 8, chip: 6, tile: 12 },
    normal: { card: 18, pill: 999, input: 14, chip: 10, tile: 16 },
    round: { card: 24, pill: 999, input: 18, chip: 999, tile: 22 },
  } as const

  return {
    ...base,
    accent,
    accentDark,
    accentSoft,
    accentBorder,
    accentOn,
    radius: radiusMap[tweaks.value.radius],
    isDark: dark,
    mode: resolvedMode.value,
    accentKey: tweaks.value.accent,
  }
})

export function useDesignSystem() {
  return {
    tweaks,
    theme,
    isDark,
    resolvedMode,
    systemDark,
    setTweak,
    patchTweaks,
  }
}

// Eagerly apply tokens to the DOM at first import so initial paint matches,
// and register the prefers-color-scheme listener once for the app lifetime.
if (typeof window !== 'undefined') {
  syncSystemDark()
  mql = window.matchMedia?.('(prefers-color-scheme: dark)') ?? null
  mql?.addEventListener?.('change', (e) => {
    systemDark.value = e.matches
  })
  if (typeof document !== 'undefined' && document.documentElement) {
    applyToDom()
  }
}
